/**
 * reportsSlice — Redux state for the three admin report views (CON-REPORT).
 *
 * Three independent report keys (sales, products, production) with separate
 * status/error so each view can fail independently. Thunks accept their param
 * sets directly so the view controls what gets fetched.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  fetchSalesReport,
  fetchProductsReport,
  fetchProductionReport,
} from '@dfq/shared/api/admin/reports';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  SalesReportDTO,
  SalesReportParams,
  ProductsReportDTO,
  ProductsReportParams,
  ProductionReportDTO,
  ProductionReportParams,
} from '@dfq/shared/types/reports';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface ReportsState {
  sales: SalesReportDTO | null;
  salesStatus: AsyncStatus;
  salesError: string | null;

  products: ProductsReportDTO | null;
  productsStatus: AsyncStatus;
  productsError: string | null;

  production: ProductionReportDTO | null;
  productionStatus: AsyncStatus;
  productionError: string | null;
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const loadSalesReport = createAsyncThunk<
  SalesReportDTO,
  SalesReportParams,
  { rejectValue: string }
>(
  'reports/loadSalesReport',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchSalesReport(params);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load sales report';
      return rejectWithValue(message);
    }
  },
);

export const loadProductsReport = createAsyncThunk<
  ProductsReportDTO,
  ProductsReportParams,
  { rejectValue: string }
>(
  'reports/loadProductsReport',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchProductsReport(params);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load products report';
      return rejectWithValue(message);
    }
  },
);

export const loadProductionReport = createAsyncThunk<
  ProductionReportDTO,
  ProductionReportParams,
  { rejectValue: string }
>(
  'reports/loadProductionReport',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchProductionReport(params);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load production report';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const initialState: ReportsState = {
  sales: null,
  salesStatus: 'idle',
  salesError: null,

  products: null,
  productsStatus: 'idle',
  productsError: null,

  production: null,
  productionStatus: 'idle',
  productionError: null,
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    resetSalesReport(state) {
      state.sales = null;
      state.salesStatus = 'idle';
      state.salesError = null;
    },
    resetProductsReport(state) {
      state.products = null;
      state.productsStatus = 'idle';
      state.productsError = null;
    },
    resetProductionReport(state) {
      state.production = null;
      state.productionStatus = 'idle';
      state.productionError = null;
    },
  },
  extraReducers: (builder) => {
    // Sales
    builder
      .addCase(loadSalesReport.pending, (state) => {
        state.salesStatus = 'loading';
        state.salesError = null;
      })
      .addCase(loadSalesReport.fulfilled, (state, action: PayloadAction<SalesReportDTO>) => {
        state.salesStatus = 'succeeded';
        state.sales = action.payload;
      })
      .addCase(loadSalesReport.rejected, (state, action) => {
        state.salesStatus = 'failed';
        state.salesError = action.payload ?? 'Failed to load sales report';
      });

    // Products
    builder
      .addCase(loadProductsReport.pending, (state) => {
        state.productsStatus = 'loading';
        state.productsError = null;
      })
      .addCase(loadProductsReport.fulfilled, (state, action: PayloadAction<ProductsReportDTO>) => {
        state.productsStatus = 'succeeded';
        state.products = action.payload;
      })
      .addCase(loadProductsReport.rejected, (state, action) => {
        state.productsStatus = 'failed';
        state.productsError = action.payload ?? 'Failed to load products report';
      });

    // Production
    builder
      .addCase(loadProductionReport.pending, (state) => {
        state.productionStatus = 'loading';
        state.productionError = null;
      })
      .addCase(loadProductionReport.fulfilled, (state, action: PayloadAction<ProductionReportDTO>) => {
        state.productionStatus = 'succeeded';
        state.production = action.payload;
      })
      .addCase(loadProductionReport.rejected, (state, action) => {
        state.productionStatus = 'failed';
        state.productionError = action.payload ?? 'Failed to load production report';
      });
  },
});

export const {
  resetSalesReport,
  resetProductsReport,
  resetProductionReport,
} = reportsSlice.actions;

export const reportsReducer = reportsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface ReportsRootState {
  reports: ReportsState;
}

export const selectSalesReport = (state: ReportsRootState) => state.reports.sales;
export const selectSalesStatus = (state: ReportsRootState) => state.reports.salesStatus;
export const selectSalesError = (state: ReportsRootState) => state.reports.salesError;

export const selectProductsReport = (state: ReportsRootState) => state.reports.products;
export const selectProductsStatus = (state: ReportsRootState) => state.reports.productsStatus;
export const selectProductsError = (state: ReportsRootState) => state.reports.productsError;

export const selectProductionReport = (state: ReportsRootState) => state.reports.production;
export const selectProductionStatus = (state: ReportsRootState) => state.reports.productionStatus;
export const selectProductionError = (state: ReportsRootState) => state.reports.productionError;
